/**
 * Adventure run state machine — `rpg/adventures/combat-flow.ts` and
 * `rewards.ts` re-expressed as pure transitions over the session's run
 * (ADR-021: the run lives in server memory, as the bot keeps it in a
 * Discord message; only the completion is written).
 *
 *   start ─▶ dialog ──advance──▶ … ──(fight dialog)──▶ fight ──attack/auto──▶ won ──advance──▶ … ─▶ complete
 *                                                         └────────────────▶ lost ──abandon──▶ (run cleared)
 */
import { chapterIndex, difficulties } from '../../data/index.js';
import { GameError } from '../../errors.js';
import type { FightLogEntry, GameEvent } from '../../events.js';
import { teamAttack } from '../../formulas/attack.js';
import {
  agiCritChance,
  agiCritDamage,
  chanceFailChance,
  intLifeSteal,
  strDamage,
  weaknessDamage,
} from '../../formulas/elements.js';
import { teamHp } from '../../formulas/power.js';
import { randInt, type Rng } from '../../rng.js';
import { premiumOf } from '../../premium.js';
import type { BotUser, Session } from '../../state.js';
import { ELEMENTS } from '../../types.js';
import { DIFFICULTY_KEYS, type DifficultyKey } from '../../types.js';
import { regenerateEnergy } from '../energy.js';
import { makeEnemy, monsterStats, stepLootcoins } from './monsters.js';
import type { AdventureRun, ChapterData, ContentProvider, EnemyState, StepData } from './types.js';

/** Safety net for auto-resolve; real fights end far sooner. */
const MAX_AUTO_ROUNDS = 500;

export function difficultyKey(difficulty: number): DifficultyKey {
  return DIFFICULTY_KEYS[difficulty] ?? 'normal';
}

/** A difficulty opens once chapter 1 of the previous one is cleared (`adventures/menu.ts`). */
export function isDifficultyUnlocked(save: BotUser, difficulty: number): boolean {
  if (difficulty <= 0) return true;
  if (difficulty >= DIFFICULTY_KEYS.length) return false;
  return save.adventures[difficultyKey(difficulty - 1)].chapter > 0;
}

/**
 * Replayable: any step up to the frontier of that difficulty. Harder
 * difficulties are capped to the chapters already cleared one level below.
 */
export function isStepUnlocked(save: BotUser, chapter: number, step: number, difficulty: number): boolean {
  const meta = chapterIndex[chapter];
  if (!meta || step < 0 || step >= meta.steps) return false;
  if (!isDifficultyUnlocked(save, difficulty)) return false;
  const progress = save.adventures[difficultyKey(difficulty)];
  const reached = chapter < progress.chapter || (chapter === progress.chapter && step <= progress.step);
  if (!reached) return false;
  if (difficulty > 0 && chapter >= save.adventures[difficultyKey(difficulty - 1)].chapter) return false;
  return true;
}

/**
 * The player's element (`classes.ts#Player`): the legacy equipped weapon's
 * `stuff.equip.element` through `getElementByName` — '' when none (parity P-2:
 * bot behavior; a new account fights with no element).
 */
export function playerElement(save: Pick<BotUser, 'stuff'>): string {
  const element = save.stuff?.equip?.element ?? '';
  if (element === 'Multi' || (ELEMENTS as readonly string[]).includes(element) || element === 'Initiative') return element;
  return '';
}

function getStep(content: ContentProvider, chapter: number, step: number): { chapterData: ChapterData; stepData: StepData } {
  const chapterData = content.getChapter(chapter);
  if (!chapterData) throw new GameError('contentNotLoaded', { chapter });
  const stepData = chapterData.steps[step];
  if (!stepData) throw new GameError('stepLocked', { chapter, step });
  return { chapterData, stepData };
}

/** `Enemy.performAttack` — roll order: damage, dice. Mutates enemy (lifesteal) and run.player.hp. */
export function enemyAttack(enemy: EnemyState, index: number, run: AdventureRun, rng: Rng, ambush = false): FightLogEntry {
  const stats = monsterStats(enemy.level);
  let jet = randInt(rng, Math.floor(enemy.damage * 0.85), Math.floor(enemy.damage * 1.15));
  jet = weaknessDamage(enemy.element, run.player.element, jet);
  jet = strDamage(enemy.element, stats, jet);
  const critChance = agiCritChance(enemy.element, stats);
  const failChance = chanceFailChance(enemy.element, stats);
  const dice = randInt(rng, 0, 100);
  let outcome: FightLogEntry['outcome'] = 'hit';
  if (dice < 10 + critChance) {
    jet = agiCritDamage(enemy.element, stats, jet);
    outcome = 'crit';
  } else if (dice >= 90 + failChance) {
    outcome = 'miss';
  }
  jet = Math.floor(jet);
  let heal = 0;
  let damage = 0;
  if (dice < 90 + failChance) {
    heal = intLifeSteal(enemy.element, stats, jet);
    if (heal > 0) {
      heal = enemy.hp + heal > enemy.baseHp ? enemy.baseHp - enemy.hp : heal;
      enemy.hp += heal;
    }
    damage = jet;
    run.player.hp -= jet;
  }
  return { actor: index, target: 'player', outcome, damage, heal, ...(ambush ? { ambush } : {}) };
}

/** `fightMonsters`: spawn enemies; with 2+ enemies one of the first two strikes first. */
function startFight(run: AdventureRun, stepData: StepData, rng: Rng, events: GameEvent[]): void {
  const dialog = stepData.dialogs[run.dialog];
  const monsters = dialog?.enemies ?? [];
  run.enemies = monsters.map((m, key) => makeEnemy(m, run.player.element, run.chapter, run.step, run.difficulty, key));
  run.phase = 'fight';
  events.push({ type: 'fightStarted', enemies: run.enemies.length });
  if (run.enemies.length >= 2) {
    const index = randInt(rng, 0, 2);
    const enemy = run.enemies[index] as EnemyState;
    events.push({ type: 'fightRound', entries: [enemyAttack(enemy, index, run, rng, true)] });
  }
}

function enterDialog(run: AdventureRun, stepData: StepData, rng: Rng, events: GameEvent[]): void {
  const dialog = stepData.dialogs[run.dialog];
  run.phase = 'dialog';
  if (dialog?.author.type === 'fight' && dialog.enemies && dialog.enemies.length > 0) startFight(run, stepData, rng, events);
}

export function startAdventure(
  save: BotUser,
  session: Session,
  chapter: number,
  step: number,
  difficulty: number,
  ctx: { now: number; rng: Rng; content: ContentProvider },
  events: GameEvent[],
): void {
  if (session.run && session.run.phase !== 'lost') throw new GameError('runInProgress');
  if (!isDifficultyUnlocked(save, difficulty)) throw new GameError('difficultyLocked', { difficulty });
  if (!isStepUnlocked(save, chapter, step, difficulty)) throw new GameError('stepLocked', { chapter, step });
  const { stepData } = getStep(ctx.content, chapter, step);

  regenerateEnergy(save, ctx.now, events);
  const cost = difficulties[difficulty]?.cost ?? 0;
  if (save.adventures.energy.current < cost) {
    throw new GameError('notEnoughEnergy', { needed: cost, have: save.adventures.energy.current });
  }
  save.adventures.energy.current -= cost;

  const hp = teamHp(save);
  const run: AdventureRun = {
    chapter,
    step,
    difficulty,
    dialog: 0,
    phase: 'dialog',
    player: { hp, baseHp: hp, element: playerElement(save), totalDamage: 0 },
    enemies: [],
    rounds: 0,
  };
  session.run = run;
  events.push({ type: 'adventureStarted', chapter, step, difficulty });
  enterDialog(run, stepData, ctx.rng, events);
}

function requireRun(session: Session): AdventureRun {
  const run = session.run;
  if (!run) throw new GameError('noRun');
  return run;
}

/** One manual round: the player strikes `target`, then every living enemy strikes back. */
export function attack(save: BotUser, session: Session, target: number, rng: Rng, events: GameEvent[]): void {
  const run = requireRun(session);
  if (run.phase !== 'fight') throw new GameError('wrongPhase', { phase: run.phase });
  const enemy = run.enemies[target];
  if (!enemy || enemy.hp <= 0) throw new GameError('invalidTarget', { target });

  const entries: FightLogEntry[] = [];
  const roll = teamAttack(save, rng);
  enemy.hp -= roll.value;
  run.player.totalDamage += roll.value;
  entries.push({ actor: 'player', target, outcome: roll.outcome, damage: roll.value, heal: 0 });

  if (run.enemies.every((e) => e.hp <= 0)) {
    run.rounds += 1;
    run.phase = 'won';
    events.push({ type: 'fightRound', entries }, { type: 'fightWon' });
    return;
  }
  run.enemies.forEach((e, i) => {
    if (e.hp > 0) entries.push(enemyAttack(e, i, run, rng));
  });
  run.rounds += 1;
  events.push({ type: 'fightRound', entries });
  if (run.player.hp <= 0) {
    run.phase = 'lost';
    events.push({ type: 'fightLost' });
  }
}

/** Whether the account may auto-fight: premium only, until the shared fix lands in both clients (T4.5). */
export function canAutoFight(save: Pick<BotUser, 'premium'>, now: number): boolean {
  return premiumOf(save, now).premium > 0;
}

/** Auto-resolve (`runAutoFightRound`): always strikes the first living enemy. */
export function autoFight(save: BotUser, session: Session, now: number, rng: Rng, events: GameEvent[]): void {
  const run = requireRun(session);
  if (run.phase !== 'fight') throw new GameError('wrongPhase', { phase: run.phase });
  if (!canAutoFight(save, now)) throw new GameError('premiumOnly');
  for (let i = 0; i < MAX_AUTO_ROUNDS && run.phase === 'fight'; i++) {
    attack(save, session, run.enemies.findIndex((e) => e.hp > 0), rng, events);
  }
  if (run.phase === 'fight') {
    run.phase = 'lost';
    events.push({ type: 'fightLost' });
  }
}

function complete(save: BotUser, session: Session, run: AdventureRun, chapterData: ChapterData, stepData: StepData, now: number, events: GameEvent[]): void {
  const lootbox = stepLootcoins(stepData, run.chapter, run.step, run.difficulty);
  const key = difficultyKey(run.difficulty);
  const progress = save.adventures[key];
  const before = { chapter: progress.chapter, step: progress.step };
  save.adventures.previous = { difficulty: run.difficulty, chapter: run.chapter, step: run.step };
  if (progress.chapter === run.chapter && run.step === chapterData.steps.length - 1) {
    progress.chapter = run.chapter + 1;
    progress.step = 0;
  } else if (progress.chapter === run.chapter && progress.step === run.step) {
    progress.step = run.step + 1;
  }
  progress.count += 1;
  save.adventures.total += 1;
  save.adventures.last = now;
  save.adventures.progress = false;
  session.run = null;
  save.lootbox += lootbox;
  save.stats.adventures.total.rewards.lootcoin += lootbox;
  save.stats.adventures[key].rewards.lootcoin += lootbox;
  const moved = before.chapter !== progress.chapter || before.step !== progress.step;
  events.push({
    type: 'adventureCompleted',
    chapter: run.chapter,
    step: run.step,
    difficulty: run.difficulty,
    lootbox,
    damage: run.player.totalDamage,
    unlocked: moved ? { chapter: progress.chapter, step: progress.step } : null,
  });
}

/** Next dialog, the fight it opens, or the step's completion and rewards. */
export function advance(save: BotUser, session: Session, ctx: { now: number; rng: Rng; content: ContentProvider }, events: GameEvent[]): void {
  const run = requireRun(session);
  if (run.phase === 'fight' || run.phase === 'lost') throw new GameError('wrongPhase', { phase: run.phase });
  const { chapterData, stepData } = getStep(ctx.content, run.chapter, run.step);
  run.dialog += 1;
  run.enemies = [];
  if (run.dialog >= stepData.dialogs.length) {
    complete(save, session, run, chapterData, stepData, ctx.now, events);
    return;
  }
  enterDialog(run, stepData, ctx.rng, events);
}

/** Leave the run; energy is not refunded and nothing is written. */
export function abandon(session: Session, events: GameEvent[]): void {
  requireRun(session);
  session.run = null;
  events.push({ type: 'adventureAbandoned' });
}
